import { OktaAuth } from "@okta/okta-auth-js"
import {
  isProtectedUrl,
  isAuthenticated,
  getShortenedUrl,
} from "@/utils/helper"

import { logout, postLogin } from "@/store/features/authSlice"
import { store } from "@/store"

let authClient

const initAuth = async config => {
  const {
    general: { isPublish = true, homepageUrl = "/" } = {},
    commerce: { sessionAuth: { securePagePathList = [] } = {} } = {},
    apiEndpoints: {
      oktaClientId = "",
      oktaIssuerUrl = "",
      oktaRedirectUri = "",
      oktaLogoutUri = "",
      loginPagePath = "",
      homeownerPagePath = "",
      installerPagePath = "",
    } = {},
  } = config || {}

  authClient = new OktaAuth({
    issuer: oktaIssuerUrl,
    clientId: oktaClientId,
    redirectUri: oktaRedirectUri,
    postLogoutRedirectUri: oktaLogoutUri,
    scopes: ["mira-session", "openid", "offline_access"],
    tokenManager: {
      autoRenew: true,
      expireEarlySeconds: 120,
    },
  })

  authClient.start()

  // subscribe for authState change
  let prevAuthState = null

  authClient.authStateManager.subscribe(authState => {
    const { isAuthenticated } = authState
    if (isAuthenticated) {
      const payload = {
        accessToken: authState.accessToken.accessToken,
        refreshToken: authState.refreshToken.refreshToken,
        user: authState.accessToken.claims || {},
        isAuth: isAuthenticated,
      }
      store.dispatch(postLogin(payload))
    }
    if (prevAuthState && prevAuthState.isAuthenticated !== isAuthenticated) {
      if (document.hidden) {
        if (!isAuthenticated) {
          return window.location.replace(oktaLogoutUri)
        } else {
          const redirect = [loginPagePath, homeownerPagePath, installerPagePath]
            .map(url => getShortenedUrl(url))
            .includes(window.location.pathname)
          if (redirect) {
            return (window.location.href = homepageUrl)
          }
        }
        window.location.reload()
      }
    }
    prevAuthState = authState
  })

  authClient.tokenManager.on("error", async function () {
    await authClient.signOut({ clearTokensBeforeRedirect: true })
    await authClient.session.close()
  })
  const isLoggedInRedirect = await authClient.isLoginRedirect()
  if (isLoggedInRedirect) {
    const { tokens } = await authClient.token.parseFromUrl()
    authClient.tokenManager.setTokens(tokens)
    const { accessToken } = tokens
    store.dispatch(postLogin(accessToken.claims))
  } else {
    authClient.authStateManager.updateAuthState()

    try {
      const accessToken = await authClient.tokenManager.get("accessToken")
      const isSessionExists = await authClient.session.exists()
      if (
        accessToken &&
        !authClient.tokenManager.hasExpired(accessToken) &&
        isSessionExists
      ) {
        const renewedToken = await authClient.tokenManager.renew("accessToken")
        store.dispatch(postLogin(renewedToken.claims))
      } else if (!accessToken) {
        const isProtected = await isProtectedUrl()

        if (isProtected && isPublish) {
          const redirectAction = {
            isLoginAction: true,
            loginCallbackURL: window.location.href,
          }
          localStorage.setItem(
            "SecurePageRedirect",
            JSON.stringify(redirectAction)
          )
          window.location.replace(
            getShortenedUrl(
              `${loginPagePath}?redirect=${btoa(window.location.href)}`
            )
          )
        } else if (localStorage.getItem("SecurePageRedirect")) {
          const redirectAction = JSON.parse(
            localStorage.getItem("SecurePageRedirect")
          )

          if (redirectAction.isLoginAction) {
            window.loginCallback = redirectAction.loginCallbackURL
            localStorage.removeItem("SecurePageRedirect")
          }
        }
      } else {
        await logout()
      }
    } catch (err) {
      console.error(
        "Okta error while getting access token from token manager",
        err
      )
    }
  }

  // anchor events
  securePagePathList.forEach(path => {
    document.querySelectorAll(`a[href="${path}"]`).forEach(tag => {
      tag.onclick = function (e) {
        e.preventDefault()
        isAuthenticated(this.href)
      }
    })
  })
}

export { initAuth, authClient }
